// @ts-nocheck
import {
  useState,
  useEffect,
  useRef,
  Fragment,
  useMemo,
  useCallback,
} from "react";
import { _getProfileDetails, _updateProfile } from "./profileAPI";
import { Toaster, toast } from "sonner";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { Dialog, Transition } from "@headlessui/react";
import {
  fetchUserData,
  updateUserProfile,
  uploadUserLogo,
} from "../../users/userSlice";
import { useAppDispatch } from "../../../app/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import timezonesData from "../../../utils/timezones.json";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "./canvasUtils";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const UserProfile = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [data, setData] = useState<any>();
  const [showAlert, setShowAlert] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [profileImage, setProfileImage] = useState("");
  const dispatch = useAppDispatch();
  const baseIdString =
    localStorage.getItem("UserBaseId") || localStorage.getItem("createBaseId");
  const baseId = baseIdString ? JSON.parse(baseIdString) : null;
  const [timezone, setTimezone] = useState("GMT");
  const [searchQuery, setSearchQuery] = useState("");
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Check if userDetails already exists in local storage
      const storedUserDetails = localStorage.getItem("userDetails");

      if (storedUserDetails) {
        // If userDetails is found in local storage, set the state from local storage
        const parsedDetails = JSON.parse(storedUserDetails);
        setName(parsedDetails.name);
        setEmail(parsedDetails.email);
        setTimezone(parsedDetails.timezone);
        setProfileImage(parsedDetails.profile_pic);
      } else {
        // If userDetails is not found, fetch data from the API
        try {
          const res = await dispatch(fetchUserData());

          if (res.payload.status === 200) {
            const userData = res.payload.data.user;

            // Set state with fetched data
            setName(userData.name);
            setEmail(userData.email);
            setTimezone(userData.timezone);
            setProfileImage(userData.profile_pic);

            // Store the fetched user data in local storage
            localStorage.setItem("userDetails", JSON.stringify(userData));
          }
        } catch (err) {
          console.log("Error fetching user data", err);
        }
      }
    };

    fetchData();
  }, [dispatch]);

  const updateProfile = () => {
    setProfileLoading(true);
    dispatch(
      updateUserProfile({
        payload: { name, timezone: timezone },
        userId: baseId,
      }),
    )
      .then(unwrapResult)
      .then((res) => {
        setProfileLoading(false);
        if (res.status === 200) {
          const baseMembersDetails = JSON.parse(
            localStorage.getItem("baseMembersDetails"),
          ) || { data: { baseMembers: [] } };
          if (baseMembersDetails?.data?.baseMembers?.length > 0) {
            const updatedMember = baseMembersDetails?.data?.baseMembers[0];

            if (updatedMember) {
              const index = baseMembersDetails.data.baseMembers.findIndex(
                (member) => member.id === updatedMember.id,
              );

              if (index !== -1) {
                baseMembersDetails.data.baseMembers[index] = updatedMember;
                localStorage.setItem(
                  "baseMembersDetails",
                  JSON.stringify(baseMembersDetails),
                );
                setData(res);
                toast.success("Profile updated successfully");
                // only update profile pic
                const existingUserDetails =
                  JSON.parse(localStorage.getItem("userDetails")) || {};

                const updatedUserDetails = {
                  ...existingUserDetails,
                  name: res.data.user.name,
                  timezone: res.data.user.timezone,
                };

                localStorage.setItem(
                  "userDetails",
                  JSON.stringify(updatedUserDetails),
                );
              } else {
                console.error("Member not found in local storage.");
              }
            }
          } else {
            setData(res);
            toast.success("Profile updated successfully");
            // only update profile pic
            const existingUserDetails =
              JSON.parse(localStorage.getItem("userDetails")) || {};

            const updatedUserDetails = {
              ...existingUserDetails,
              name: res.data.user.name,
              timezone: res.data.user.timezone,
            };

            localStorage.setItem(
              "userDetails",
              JSON.stringify(updatedUserDetails),
            );
          }
        }
      })
      .catch((err) => {
        setProfileLoading(false); // Set loading to false in case of an error
      });
  };

  useEffect(() => {
    if (profileImage) {
      setLogoLoading(true);
      const newLogoUrl = `${process.env.REACT_APP_API_BASE_URL}/${profileImage}`;
      setLogoUrl(newLogoUrl);

      // Preload the image
      const img = new Image();
      img.onload = () => {
        setLogoLoading(false);
      };
      img.onerror = () => {
        setLogoLoading(false);
        setLogoUrl("");
      };
      img.src = newLogoUrl;
    } else {
      setLogoUrl("");
      setLogoLoading(false);
    }
  }, [profileImage]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      setCropSrc(URL.createObjectURL(selectedFile));
      setShowCropModal(true);
    }
  };

  const onImageLoaded = (event: React.SyntheticEvent<HTMLImageElement>) => {
    imageRef.current = event.currentTarget;
    setCrop(
      centerCrop(
        makeAspectCrop(
          { unit: "px", width: 250, aspect: 1 },
          1,
          imageRef.current.width,
          imageRef.current.height,
        ),
      ),
    );
  };

  const getCroppedImage = (): Promise<File | null> => {
    if (!completedCrop || !imageRef.current || !canvasRef.current || !file) {
      return Promise.resolve(null);
    }

    const image = imageRef.current;
    const canvas = canvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return Promise.resolve(null);
    }

    canvas.width = 250;
    canvas.height = 250;

    return new Promise((resolve) => {
      if (image.complete) {
        drawCanvas();
      } else {
        image.onload = drawCanvas;
      }

      function drawCanvas() {
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          250,
          250,
        );

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            const croppedFile = new File([blob], file.name, {
              type: file.type,
            });
            resolve(croppedFile);
          },
          file.type,
          1.0,
        );
      }
    });
  };

  const handleCropSave = async () => {
    setLoading(true);
    try {
      // Helper function to handle the canvas work
      const croppedImageBlob = await getCroppedImg(cropSrc, croppedAreaPixels);
      const croppedFile = new File([croppedImageBlob], "profile.jpeg", {
        type: "image/jpeg",
      });

      const result = await dispatch(
        uploadUserLogo({ payload: { image: croppedFile }, userId: baseId }),
      );

      if (uploadUserLogo.fulfilled.match(result)) {
        toast.success("Upload successful!");
        const newUrl = `${process.env.REACT_APP_API_BASE_URL}/${result.payload.data.profile_pic}`;
        setLogoUrl(newUrl);

        const existingUserDetails =
          JSON.parse(localStorage.getItem("userDetails")) || {};
        const updatedUserDetails = {
          ...existingUserDetails,
          profile_pic: result.payload.data.profile_pic,
        };
        localStorage.setItem("userDetails", JSON.stringify(updatedUserDetails));
        setLogoLoading(false);
      } else {
        throw new Error(result.payload || "Upload failed");
      }

      window.dispatchEvent(new Event("userProfileDetailsUpdated"));
    } catch (error) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setLoading(false);
      setShowCropModal(false);
    }
  };

  const timezones = timezonesData.map((tz) => ({
    // value: tz.value,  // Make sure this exists
    label: tz.text,
    abbr: tz.abbr,
    utc: tz.utc,
  }));

  // Add this filtering logic
  const filteredTimezones = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return timezones;

    return timezones.filter(
      (zone) =>
        zone.abbr.toLowerCase().includes(query) ||
        zone.label.toLowerCase().includes(query) ||
        zone.utc.some((utc) => utc.toLowerCase().includes(query)),
    );
  }, [timezones, searchQuery]);

  const handleTimeZoneChange = (newTimeZone: string) => {
    setTimezone(newTimeZone);
    // localStorage.setItem('timeZone', newTimeZone);
  };

  return (
    <>
      <main className="py-[34px] lg:py-[70px] px-0 lg:px-10 h-full flex">
        <div className="px-[2px] sm:px-6 lg:px-8 w-full flex justify-center">
          <div className="w-full max-w-[640px]">
            <div className="flex flex-col gap-y-[6px]">
              <h2 className="text-[20px] lg:text-[24px] font-medium leading-8 text-[#1b1b1b] tracking-[-0.01rem]">
                Profile
              </h2>
              <p className="text-sm leading-6 text-[#5c5c5e] font-medium">
                This information will be displayed publicly so be careful what
                you share.
              </p>
            </div>
            <div className="bg-[#ededed] h-[1px] w-full my-8"></div>
            <div className="flex items-center mt-6 lg:mt-8 justify-between relative">
              <label className="flex flex-inherit h-[70px] w-[70px] cursor-pointer z-10 absolute top-[0px] left-[0px] cursor-move">
                <input
                  className="opacity-0 focus:outline-none cursor-move"
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </label>
              <div className="absolute top-[0px] left-[0px] h-[70px] w-[70px]">
                {logoLoading ? (
                  <div className="flex items-center justify-center bg-[#f5f5f5] rounded-full h-full">
                    <div className="simple-spinner-loader">
                      <span className="!w-4 !h-4"></span>
                    </div>
                  </div>
                ) : logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Base Logo"
                    className="h-full w-full border-[5px] border-solid border-[#f5f5f5] rounded-full object-cover cursor-move"
                    onLoad={() => setLogoLoading(false)}
                  />
                ) : (
                  <div className="h-full w-full border-[5px] border-solid border-[#f5f5f5] rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">Logo</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-[76px] lg:mt-[6rem]">
              <label
                htmlFor="text"
                className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5"
              >
                Name
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="text"
                  placeholder="Enter Your Name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex  grow border px-4 py-2 w-[350px] text-sm leading-6 shadow-[0 1px 2px 0 rgba(18, 18, 23, 0.05)] transition-colors duration-100 placeholder:text-surface-500 outline-indigo-600
                                    focus:outline focus:outline-2 focus:-outline-offset-2 bg-background text-text-100 hover:border-[#a9a9bc] dark:hover:border-surface-200
                                    border-[#d1d1db] dark:border-surface-100 focus:outline-indigo-600 h-[36px] rounded-[0.375rem]"
                />
              </div>
            </div>
            <div className="my-5">
              <label
                htmlFor="email"
                className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5"
              >
                Email
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="Enter Your Email"
                  required
                  disabled
                  className="flex  text-[#a9a9bc] w-[350px] grow rounded-md border px-4 py-2 text-sm leading-6 shadow-wg-xs hover:border-[#a9a9bc] 
                                    dark:hover:border-surface-200 border-[#d1d1db] transition-colors duration-100 outline-primary focus:outline focus:outline-2 
                                    focus:-outline-offset-2 cursor-not-allowed bg-surface-50 text-surface-300 placeholder:text-surface-300 dark:bg-white/5
                                     dark:text-surface-200 dark:placeholder:text-surface-200 
                                    border-surface-200 dark:border-surface-100 focus:outline-indigo-600 h-[36px]"
                />
              </div>
            </div>
            <div className="flex flex-col justify-start items-start gap-y-[10px] mt-7 w-[350px]">
              <label
                htmlFor="name"
                className="wg-label inline-flex shrink-0 items-center gap-1 text-surface-900 wg-antialiased"
              >
                Timezone
              </label>
              <div className="w-full">
                <Listbox value={timezone} onChange={setTimezone}>
                  {({ open }) => (
                    <div className="relative mt-[-5px] text-right flex justify-end">
                      <ListboxButton
                        className="w-[100%] inline-flex items-center justify-between whitespace-nowrap rounded-md font-normal
                                                               text-sm  transition-colors outline-offset-2 focus-visible:outline focus-visible:outline-2
                                                               focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 
                                                               border border-input bg-background shadow-[0 1px 2px 0 rgba(18, 18, 23, 0.05)] hover:bg-accent hover:text-accent-foreground h-[36px] px-3 py-2 hover:border-[#a9a9bc] dark:hover:border-surface-200
                                         border-[#d1d1db]"
                      >
                        <span className="block truncate line-clamp-1">
                          {timezone ? (
                            timezone
                          ) : (
                            <span className="text-[#9ca3af] font-normal">
                              Select Timezone
                            </span>
                          )}
                        </span>
                      </ListboxButton>

                      <ListboxOptions
                        className="  absolute top-[38px] z-10 mt-[2px] left-[0px] bg-white w-full max-w-full min-w-full rounded-lg border border-border
                                         bg-popover p-1 text-popover-foreground shadow-lg shadow-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out
                                           data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
                                                                                            data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2
                                                                                             data-[side=top]:slide-in-from-bottom-2"
                      >
                        <div className="sticky top-0 z-10 bg-white p-1">
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full px-3 py-1 border mt-1.5 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                              placeholder="Search timezone..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) =>
                                e.key === " " && e.stopPropagation()
                              }
                            />
                            <div className="absolute top-[6px] bottom-0 right-0 flex items-center pr-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={16}
                                height={16}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-gray-400"
                              >
                                <path
                                  stroke="none"
                                  d="M0 0h24v24H0z"
                                  fill="none"
                                />
                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                                <path d="M21 21l-6 -6" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="overflow-auto max-h-[180px] mt-1">
                          {filteredTimezones.length > 0 ? (
                            filteredTimezones.map((tz) => (
                              <ListboxOption
                                key={tz.label}
                                value={tz.label}
                                className={({ active }) =>
                                  classNames(
                                    active
                                      ? "bg-gray-200 text-[#1d2025]"
                                      : "text-gray-900",
                                    "relative flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                                  )
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                                    >
                                      {tz.label}
                                    </span>
                                    {selected && (
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width={24}
                                          height={24}
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth={2}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="w-4 h-4"
                                        >
                                          <path
                                            stroke="none"
                                            d="M0 0h24v24H0z"
                                            fill="none"
                                          />
                                          <path d="M5 12l5 5l10 -10" />
                                        </svg>
                                      </span>
                                    )}
                                  </>
                                )}
                              </ListboxOption>
                            ))
                          ) : (
                            <div className="px-2 py-1.5 text-sm text-gray-500 text-center">
                              No timezone found
                            </div>
                          )}
                        </div>
                      </ListboxOptions>
                    </div>
                  )}
                </Listbox>
              </div>
            </div>
            <div className="flex justify-start mt-8">
              <button
                onClick={updateProfile}
                type="submit"
                className="flex  justify-center rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                size="sm"
              >
                {profileLoading ? (
                  <div className="flex items-center  gap-[6px]">
                    Update
                    <div className="simple-spinner">
                      <span></span>
                    </div>
                  </div>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      <Transition.Root show={showCropModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={() => setShowCropModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="relative h-[400px] w-full bg-[#333]">
                    <Cropper
                      image={cropSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                  {/* Optional Zoom Slider */}
                  <div className="mt-4">
                    <input
                      type="range"
                      value={zoom}
                      min={1}
                      max={3}
                      step={0.1}
                      aria-labelledby="Zoom"
                      onChange={(e) => setZoom(e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div className="flex justify-end mt-5 gap-3">
                    <button
                      onClick={() => setShowCropModal(false)}
                      className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCropSave}
                      disabled={loading}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          Saving <div className="simple-spinner border-white" />
                        </div>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
export default UserProfile;
